import { Box } from "@biom3/react";
import type { Content } from "@prismicio/client";
import { trackGameDetailsEvent } from "@ui-kit/utils";

import { useEffect, useState } from "react";
import { MAIN_QUESTS_GROUP } from "../../../constants";
import { QuestList } from "./QuestList";
import { QuestModal, type QuestModalProps } from "./QuestModal";
import type { QuestGroupState, QuestState } from "./types";

const getModalDismissalKey = (
  userId: string,
  gameId: string,
  groupKey: string,
  type: "intro" | "exit",
) => `quest-modal-dismissed-${userId}-${gameId}-${groupKey}-${type}`;

export type QuestGroupsProps = {
  groups: QuestGroupState[];
  game: Content.GameDocument;
  allGroupsCompleted: boolean;
  onQuestClick: (questState: QuestState) => void;
  onQuestLockTimerExpired?: (quest: QuestState) => void;
  isWishlisted: boolean;
  userId: string;
  useVariantBExperiment?: boolean;
};

export function QuestGroups({
  groups,
  game,
  onQuestClick,
  onQuestLockTimerExpired,
  allGroupsCompleted,
  isWishlisted,
  userId,
  useVariantBExperiment = false,
}: QuestGroupsProps) {
  const [activeGroupIndex, setActiveGroupIndex] = useState<number>(-1);
  const [modalState, setModalState] = useState<
    Omit<QuestModalProps, "onClose">
  >({
    visible: isWishlisted,
    title: "",
    description: "",
    foregroundImage: "",
    game,
  });

  const onModalClose = () => {
    setModalState((prev) => ({ ...prev, visible: false }));

    // Store dismissal state in local storage
    if (activeGroupIndex !== -1) {
      const activeGroup = groups[activeGroupIndex];
      const isExitModal = groups.every((group) =>
        group?.quests.every((q: QuestState) => q.completed),
      );

      const modalType = isExitModal ? "exit" : "intro";
      const dismissalKey = getModalDismissalKey(
        userId,
        game.id,
        activeGroup.key,
        modalType,
      );
      localStorage.setItem(dismissalKey, "true");
    }
  };

  const onAllQuestsCompleted = () => {
    trackGameDetailsEvent({
      identifier: "QuestList",
      control: "QuestsCompleted",
      controlType: "MenuItem",
      action: "Pressed",
      game,
    });
  };

  const isGroupCompleted = (key: string) =>
    groups
      .find((g) => g.key === key)
      ?.quests.every((q: QuestState) => q.completed);

  useEffect(() => {
    if (groups.length < 2) return;

    const firstIncompleteGroupIndex = groups.findIndex(
      (g) => !g.quests.every((q: QuestState) => q.completed),
    );
    if (firstIncompleteGroupIndex !== -1) {
      setActiveGroupIndex(firstIncompleteGroupIndex);
    }
  }, [groups]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (activeGroupIndex === -1) return;

    const activeGroup = groups?.[activeGroupIndex];
    if (!activeGroup || !activeGroup?.introModal?.title) return;

    const completedQuestsCount = activeGroup.quests.filter(
      (q: QuestState) => q.completed,
    ).length;

    // Check if modal was previously dismissed
    const dismissalKey = getModalDismissalKey(
      userId,
      game.id,
      activeGroup.key,
      "intro",
    );
    const wasModalDismissed = localStorage.getItem(dismissalKey) === "true";

    // modal visibility depends on the active group index, completed quests count, and dismissal state
    const visible =
      !wasModalDismissed &&
      (activeGroupIndex === 0
        ? completedQuestsCount <= 1
        : completedQuestsCount === 0);

    setModalState((prev) => ({
      ...prev,
      visible,
      ...activeGroup.introModal,
    }));
  }, [activeGroupIndex, userId]);

  useEffect(() => {
    if (activeGroupIndex === -1) return;

    const activeGroup = groups?.[activeGroupIndex];
    if (!activeGroup?.exitModal) return;

    const allGroupsCompleted = groups.every((group) =>
      group?.quests.every((q: QuestState) => q.completed),
    );

    if (allGroupsCompleted) {
      // Check if exit modal was previously dismissed
      const dismissalKey = getModalDismissalKey(
        userId,
        game.id,
        activeGroup.key,
        "exit",
      );
      const wasModalDismissed = localStorage.getItem(dismissalKey) === "true";

      setModalState((prev) => ({
        ...prev,
        visible: !wasModalDismissed,
        ...activeGroup.exitModal,
      }));
    }
  }, [groups, activeGroupIndex, game.id, userId]);

  return (
    <Box
      sx={{
        w: {
          default: "100%",
          xLarge: useVariantBExperiment ? "100%" : "74.5%",
        },
        px: {
          default: "none",
          large: useVariantBExperiment ? "0px" : "base.spacing.x12",
        },
        maxWidth: "800px",
      }}
      testId="QuestGroups"
    >
      <QuestModal {...modalState} onClose={onModalClose} />
      {groups
        .filter((g) => {
          if (!g.showOnAllCompleted) {
            return true;
          }

          if (g.showOnAllCompleted && allGroupsCompleted) {
            return true;
          }

          return false;
        })
        .map((group, index) => {
          const locked =
            index > 0 &&
            group.lockedUntilPrevious &&
            !isGroupCompleted(groups[index - 1]?.key);

          return (
            <QuestList
              key={group.key}
              game={game}
              quests={group.quests}
              title={group.header?.title}
              caption={group.header?.description}
              locked={locked}
              totalGemsGranted={group.totalGemsGranted}
              onQuestClick={onQuestClick}
              onQuestLockTimerExpired={onQuestLockTimerExpired}
              onAllQuestsCompleted={onAllQuestsCompleted}
              nextQuest={group.nextQuest}
              hideOnCompletion={group.hideOnCompletion}
              mainQuests={group.questGroup === MAIN_QUESTS_GROUP}
              progressBadge={{
                icon: group.progressIcon,
                template: group.progressTemplate,
              }}
            />
          );
        })}
    </Box>
  );
}
